import { useBridge } from '@acsbe/commons';
import {
  Flex,
  fromTheme,
  ModalStack,
  usePlatformProps,
  useToast,
} from '@acsbe/ui';
import HTTP_ERRORS from 'http-status';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isRouteErrorResponse,
  Navigate,
  Outlet,
  useLocation,
  useRouteError,
} from 'react-router-dom';
import { styled } from 'styled-components';
import {
  APPLICATION_MODES,
  CmsDomain,
  useApplicationViewState,
  useBilling,
  useCompleteLogout,
  useDomainSelection,
  useMerchantSession,
  useOwnershipClaim,
  VIEW_STATES,
} from '../../context';
import { useRefreshUserData } from '../../hooks/useRefreshUserData';
import { useAnalytics } from '../../services/analytics';
import { PAGE_VIEW } from '../../services/analytics/event-names';
import { TrialExpiredPopup } from '../../ui';
import { ReviewModal } from '../../ui/ReviewModal';
import { MESSAGE_EVENT, sendMessageToWrapper } from '../../utils';
import { isUserLoggedIn } from '../../utils/auth-utils';
import axios from '../../utils/axiosInstance';
import {
  CMS,
  CMS_CUSTOM_DISCOUNTS,
  CMS_WITH_USER_FEEDBACK,
} from '../../utils/constants';
import { Logger } from '../../utils/logger';
import { transformDomainPopup } from '../../validations/common';
import { Billing } from './billing';
import { ChooseAPlanAction } from './subscription/choose-plan-button';
import { TabList } from './tablist';

export const path = '/';

export const id = 'root';

let isInterceptorRegistered = false;
export function Page() {
  const MANAGE_TABID = 'manage-license';
  const referrer = useRef(document.referrer);
  const analytics = useAnalytics();
  const { pathname } = useLocation();
  const [isWidgetEnabled, setIsWidgetEnabled] = useState(false);
  const [showOAuthSuccessMessage, setShowOAuthSuccessMessage] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [planStatus, setPlanStatus] = useState('');
  const [planName, setPlanName] = useState('');
  const [currentTab, setCurrentTab] = useState(MANAGE_TABID);
  const {
    merchantDetails,
    setMerchantDetails,
    setCmsContextToken,
    setRedirectUrl,
    setAccessibeUser,
    setPortalMixpanelProps,
    feedbackVisible,
    userFeedback,
  } = useMerchantSession();

  const { completeLogout: onLogout } = useCompleteLogout();

  const { wooPendingPayment } = useBilling();

  const { applicationViewState } = useApplicationViewState();

  const {
    licenseDetails,
    licenseId,
    setCurrentAccessibeAccount,
    setAllDomains,
    setDomainsFetched,
    setIsCustomDiscount,
  } = useDomainSelection();
  const { setOwnershipAccessRequest } = useOwnershipClaim();
  const p = usePlatformProps();
  const [showTrialExpiredPopup, setShowTrialExpiredPopup] = useState(false);
  const { refreshUserData } = useRefreshUserData();

  const updateCurrentTab = (tab: string) => {
    setCurrentTab(tab);
  };

  const _handlePostMessageEvent = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (event: any) => {
      if (event.data.data?.mixpanelProps) {
        Logger.debug('setting mixpanelProps:', event.data.data.mixpanelProps);
        Object.assign(analytics.mixpanel.defaultProperties, {
          ...event.data.data.mixpanelProps,
        });
        setPortalMixpanelProps(event.data.data.mixpanelProps);
        delete event.data.data['mixpanelProps'];
      }

      if (event.data.eventName === 'merchantDetails') {
        Logger.debug('- merchantDetails Event -', event.data.data);

        if (event.data.additionalData?.showOAuthSuccessMessage) {
          setShowOAuthSuccessMessage(true);
        }

        // don't set licenseId if user is not logged in. Let him choose after login/signup
        setMerchantDetails(
          isUserLoggedIn()
            ? event.data.data
            : { ...event.data.data, licenseId: '' },
        );

        if (event.data.data.widgetStatus) {
          setIsWidgetEnabled(true);
        }
        analytics.mixpanel.defaultProperties.pluginSource =
          event.data.data?.source;

        const { href } = window.location;
        referrer.current = href;
      } else if (event.data.eventName === 'cmsContext') {
        Logger.log('- setting cmsContext -');
        // TODO: move to axiosInstance
        axios.defaults.headers.common['cms-context'] = event.data.data;
        setCmsContextToken(event.data.data);
      } else if (event.data.eventName === 'redirect-url') {
        Logger.log('setting redirect-url', event.data.data);
        setRedirectUrl(event.data.data);
      } else if (event.data.eventName === 'wait-for-oauth') {
        // TODO: remove this event from square
      } else if (event.data.eventName === 'request-logout') {
        await onLogout();
      } else if (event.data.eventName === 'domain-verification-request') {
        const { domain, path, type } = event.data.data;
        setOwnershipAccessRequest({ domain, path, type });
      } else if (event.data.eventName === 'build-cms-context') {
        const { source, accessToken } = event.data.data;
        switch (source) {
          case CMS.WIX: {
            const response = await axios.post('/api/wix/context/create', {
              accessToken,
            });
            Logger.debug('create context:', response.data);
            if (response.data.cmsContext) {
              setCmsContextToken(response.data.cmsContext);
            }
            break;
          }
          default:
            break;
        }
      } else if (event.data.eventName == 'domains-fetched') {
        const result = event.data.data
          ?.filter((obj: CmsDomain) => obj?.domain)
          ?.map((obj: CmsDomain) => {
            return { ...obj, domain: transformDomainPopup(obj.domain) };
          });
        setDomainsFetched(true);
        setAllDomains(result ?? []);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analytics.mixpanel, pathname, onLogout],
  );

  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    const showToast = (message: string) => {
      const ToastContent = (props: { message: string }) => {
        return (
          <Flex
            row
            paddingInline={4}
            paddingBlock={1.5}
            align="center"
            radius="xl"
          >
            {t(props.message)}
          </Flex>
        );
      };
      toast(<ToastContent message={message} />, {
        scale: 'danger',
        duration: 5000,
        icon: 'Warning',
      });
    };

    if (isInterceptorRegistered) return;
    isInterceptorRegistered = true;
    // TODO: move to axiosInstance
    axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        if (error.response?.status === 401) {
          Logger.log('- interceptor - got 401');
          if (isUserLoggedIn()) await onLogout();
        } else {
          Logger.log('- interceptor - got error', error);
        }
        return Promise.reject(error);
      },
    );
  }, [toast, t, onLogout]);

  useEffect(() => {
    window.addEventListener('message', _handlePostMessageEvent);
  }, [_handlePostMessageEvent]);

  useEffect(() => {
    sendMessageToWrapper(MESSAGE_EVENT.IFRAME_READY);
  }, []);

  useEffect(() => {
    if (merchantDetails?.source) {
      setIsLoaded(true);
    }
  }, [merchantDetails?.source]);

  useEffect(() => {
    if (applicationViewState !== VIEW_STATES.BLANK && merchantDetails?.source) {
      let pageName;
      if (applicationViewState === VIEW_STATES.DASHBOARD) {
        if (currentTab === MANAGE_TABID) {
          pageName = 'plan and payment';
        } else {
          pageName = currentTab?.toLowerCase()?.replace('-', ' ');
        }
      } else {
        pageName = applicationViewState?.toLowerCase()?.replace('_', ' ');
      }
      analytics.mixpanel.register({
        pageName: pageName,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const properties: any = {
        prevUrl: referrer.current,
      };
      properties['announcementLoaded'] = 'Not Presented';
      properties['announcementType'] = '';
      analytics.mixpanel.trackEvent(PAGE_VIEW, properties);
    }
  }, [
    applicationViewState,
    analytics.mixpanel,
    merchantDetails?.source,
    currentTab,
  ]);

  useEffect(() => {
    if (licenseDetails) {
      setPlanStatus(licenseDetails.status);
      setPlanName(licenseDetails.plan.name ?? '');

      if (licenseDetails.status === 'expired' && !licenseDetails.plan?.name) {
        if (wooPendingPayment === false) {
          setShowTrialExpiredPopup(true);
        }
      } else {
        setShowTrialExpiredPopup(false);
      }

      if (
        licenseDetails?.discountEnabled &&
        merchantDetails?.source &&
        merchantDetails.source in CMS_CUSTOM_DISCOUNTS &&
        licenseDetails?.finalDiscount ===
          CMS_CUSTOM_DISCOUNTS[merchantDetails?.source]
      ) {
        setIsCustomDiscount(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseDetails, wooPendingPayment, merchantDetails?.source]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (isUserLoggedIn() || licenseId) {
        const data = await refreshUserData();
        if (!data) return;
        setCurrentAccessibeAccount(data.account);
        setAccessibeUser(data.user);

        const currentDistinctId = analytics.mixpanel.getDistinctId();

        if (currentDistinctId !== data.user.userId) {
          analytics.mixpanel.identify(data.user.userId);
          analytics.mixpanel.register({
            accountId: data.account._id,
          });
        }
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    analytics.mixpanel,
    licenseId,
    setCurrentAccessibeAccount,
    setAccessibeUser,
  ]);

  useEffect(() => {
    sendMessageToWrapper(MESSAGE_EVENT.DOMAIN_LIST, {
      domains: [],
    });
  }, []);

  const { applicationMode } = useApplicationViewState();
  return (
    <>
      <iframe
        src={`${import.meta.env.VITE_DASHBOARD_URL}/be/v1/cms-app/dashboard-session`}
        id="dashboard-session"
        title="accessi.org"
        width="1"
        height="1"
        style={{ display: 'none' }}
      />
      {showTrialExpiredPopup && (
        <TrialExpiredPopup
          onClose={() => {
            setShowTrialExpiredPopup(false);
          }}
          onChoosePlan={() => {
            setShowTrialExpiredPopup(false);
            ChooseAPlanAction();
          }}
        />
      )}
      {merchantDetails?.source &&
        CMS_WITH_USER_FEEDBACK.includes(merchantDetails.source) &&
        feedbackVisible &&
        userFeedback && <ReviewModal />}
      <ModalStack>
        {isLoaded && (
          <MainContainer scale="base">
            {applicationViewState === VIEW_STATES.BILLING &&
            applicationMode !== APPLICATION_MODES.LIMITED ? (
              <Billing />
            ) : (
              <TabList
                isWidgetEnabled={isWidgetEnabled}
                setIsWidgetEnabled={setIsWidgetEnabled}
                planName={planName}
                planStatus={planStatus}
                updateCurrentTab={updateCurrentTab}
              />
            )}
            <Outlet
              context={{
                isWidgetEnabled,
                setIsWidgetEnabled,
                showOAuthSuccessMessage,
              }}
            />
          </MainContainer>
        )}
      </ModalStack>
    </>
  );
}
const MainContainer = styled(Flex)`
  min-height: 100vh;
  background-color: ${fromTheme('palette.scales.base.background.static')};
`;

function ErrorBoundary() {
  const error = useRouteError();
  const bridge = useBridge();

  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case HTTP_ERRORS.NOT_FOUND: {
        return <Navigate to="/404" replace />;
      }
    }
  }
  console.error(error);
  bridge.redirectToLegacy?.('/');

  return null;
}

export const errorElement = <ErrorBoundary />;
